/**
 * What the server hands back when it speaks a turn (2026-09-21). Either the whole reply as one
 * blob, or — from a provider that streams — raw samples arriving as they are made, which a person
 * can start hearing a second or more before the provider has finished speaking.
 *
 * Nothing here keeps audio: a stream is read once and its pieces are dropped as they are played.
 */
export interface PcmStream {
  readonly kind: 'pcm-stream';
  /** 16-bit little-endian mono samples at this rate. */
  readonly sampleRate: number;
  readonly chunks: ReadableStream<Uint8Array>;
}

export type SpokenAudio = Blob | PcmStream;

export function isPcmStream(audio: SpokenAudio): audio is PcmStream {
  return !(audio instanceof Blob);
}

const DEFAULT_RATE = 24_000;

/** The stream a speech response carries, or null when it is an ordinary audio file. */
export function pcmStreamFrom(response: {
  readonly headers: { get(name: string): string | null };
  readonly body: ReadableStream<Uint8Array> | null;
}): PcmStream | null {
  const type = (response.headers.get('content-type') ?? '').toLowerCase();
  if (!type.startsWith('audio/pcm') || response.body === null) return null;
  const stated = Number(/(?:^|;)\s*rate=(\d{4,6})\b/u.exec(type)?.[1]);
  return {
    kind: 'pcm-stream',
    sampleRate:
      Number.isInteger(stated) && stated >= 8000 && stated <= 192_000 ? stated : DEFAULT_RATE,
    chunks: response.body,
  };
}

/**
 * The whole stream as one WAV file, for a browser with no Web Audio to schedule it with. Slower to
 * start — it waits for the last sample — and otherwise the same sound.
 */
export async function wavBlobFrom(stream: PcmStream, signal?: AbortSignal): Promise<Blob> {
  const reader = stream.chunks.getReader();
  const pieces: Uint8Array[] = [];
  let length = 0;
  try {
    for (;;) {
      if (signal?.aborted === true) break;
      const { done, value } = await reader.read();
      if (done) break;
      pieces.push(value);
      length += value.length;
    }
  } catch {
    // What arrived is what plays.
  } finally {
    void reader.cancel().catch(() => undefined);
  }
  const data = length - (length % 2);
  const header = new DataView(new ArrayBuffer(44));
  const ascii = (offset: number, text: string): void => {
    for (let index = 0; index < text.length; index += 1)
      header.setUint8(offset + index, text.charCodeAt(index));
  };
  ascii(0, 'RIFF');
  header.setUint32(4, 36 + data, true);
  ascii(8, 'WAVEfmt ');
  header.setUint32(16, 16, true);
  header.setUint16(20, 1, true);
  header.setUint16(22, 1, true);
  header.setUint32(24, stream.sampleRate, true);
  header.setUint32(28, stream.sampleRate * 2, true);
  header.setUint16(32, 2, true);
  header.setUint16(34, 16, true);
  ascii(36, 'data');
  header.setUint32(40, data, true);
  const body = new Uint8Array(data);
  let at = 0;
  for (const piece of pieces) {
    const take = Math.min(piece.length, data - at);
    body.set(piece.subarray(0, take), at);
    at += take;
  }
  return new Blob([header.buffer, body.buffer], { type: 'audio/wav' });
}
